import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FaArrowLeft,
    FaChevronRight,
    FaExclamationTriangle,
    FaFileAlt,
} from "react-icons/fa";
import { loadsResource, reportResource } from "../../helpers/configRoutes/backend_helper";
import PreviewPDF from "../../components/modal/PreviewPDF";
import { useDispatch } from "react-redux";
import { update_report } from "../../store/reports/action";
import ReportModal from "../ServiceRequest/ReportModal";

const LoadDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [load, setLoad] = useState(null);
    const [reports, setReports] = useState([]);
    const [modal, setModal] = useState({ show: false });
    const [modalReport, setModalReport] = useState({ show: false });
    const [filters, setFilters] = useState({ perPage: 10, page: 1 });

    useEffect(() => {
        if (!id) return;
        const fetchData = async () => {
            const loadRes = await loadsResource("show", {}, id);
            const reportsRes = await reportResource("get", { params: { load_id: id } });
            setLoad(loadRes.load);
            setReports(reportsRes.reports || []);
        };
        fetchData();
    }, [id, filters]);

    if (!load) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-cs-bg-alt gap-4">
                <div className="w-10 h-10 border-4 border-cs-primary border-t-transparent animate-spin rounded-full" />
                <p className="text-cs-muted animate-pulse font-medium tracking-wide">Fetching load details...</p>
            </div>
        );
    }

    const isLoadCompleted = load.status_id === 29 || (load.stops?.length > 0 && load.stops.every((s) => s.status_operator_id === 33));
    const nextStop = isLoadCompleted
        ? null
        : load.stops?.find((s) => s.can?.arrive || s.can?.depart || s.can?.complete) || load.first_stop;
    const completedStops = load.stops?.filter((s) => s.status_operator_id === 33).length || 0;

    const getStopStateMeta = (stop, isNext) => {
        if (stop.status_operator_id === 33) {
            return {
                badge: "Completed",
                badgeClass: "bg-cs-success text-white",
                helper: "This stop is already completed.",
            };
        }

        if (isNext) {
            return {
                badge: "Ready now",
                badgeClass: "bg-cs-primary text-white",
                helper: "Open this stop to continue the workflow and unlock the action button.",
            };
        }

        return {
            badge: "Locked",
            badgeClass: "bg-cs-bg-alt text-cs-muted border border-cs-border",
            helper: "Complete the current next stop first.",
        };
    };

    const newReport = () => {
        const defaultStop = load.stops?.find((stop) => stop.can?.arrive || stop.can?.depart || stop.can?.complete)
            || load.stops?.[0]
            || null;

        dispatch(update_report({
            description: "",
            status_id: "",
            stop_id: defaultStop?.id || "",
            service_order_id: "",
            trailer_id: defaultStop?.effective_trailer_id || defaultStop?.trailer_id || load.trailer_id,
            truck_id: load.truck_id,
            trailer_failure: false,
            truck_failure: false,
        }));
        setModalReport({ show: true, stops: load.stops, title: "Create Incident Report" });
    };

    return (
        <div className="min-h-screen bg-cs-bg-alt pb-safe font-sans">
            {/* STICKY HEADER */}
            <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md px-4 py-4 border-b border-cs-border flex items-center">
                <button
                    onClick={() => navigate("/loads")}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-cs-bg-alt text-cs-muted active:scale-90 transition-transform"
                >
                    <FaArrowLeft size={16} />
                </button>
                <div className="flex-1 text-center pr-10">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-cs-muted font-bold">Details</p>
                    <h1 className="font-bold text-cs-dark">Load #{load.id}</h1>
                </div>
            </div>

            <div className="px-4 py-6 flex flex-col gap-6">

                {/* STATUS BAR */}
                <div className="bg-cs-primary-soft border border-cs-primary/10 rounded-2xl px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-cs-primary animate-pulse" />
                        <span className="text-[11px] font-bold text-cs-primary-dark uppercase tracking-wider">Current Status</span>
                    </div>
                    <p className="text-xs font-extrabold text-cs-primary uppercase">
                        {isLoadCompleted ? (load.status?.name || 'Finished') : (nextStop?.status?.name || load.status?.name)}
                    </p>
                </div>

                <div className="bg-white rounded-3xl shadow-soft p-4 border border-cs-border/60">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <p className="text-[10px] font-bold text-cs-muted uppercase tracking-[0.2em]">{isLoadCompleted ? 'Workflow result' : 'Next stop to work'}</p>
                            <p className="text-base font-bold text-cs-dark mt-1">{isLoadCompleted ? 'Load completed' : (nextStop?.name || 'Route in progress')}</p>
                            <p className="text-xs text-cs-muted mt-2">
                                {isLoadCompleted
                                    ? 'All stops were completed successfully and the load is now marked as finished.'
                                    : 'Only the next stop becomes actionable. Remaining stops stay locked until this one is completed.'}
                            </p>
                        </div>
                        <div className="text-right shrink-0">
                            <p className="text-[10px] font-bold text-cs-muted uppercase tracking-[0.15em]">Progress</p>
                            <p className="text-lg font-black text-cs-primary">{completedStops}/{load.stops?.length || 0}</p>
                        </div>
                    </div>
                </div>

                {/* ROUTE INFO CARD */}
                <div className="bg-white rounded-3xl shadow-soft p-5 border border-white">
                    <div className="flex justify-between items-start mb-4">
                        <div className="space-y-1">
                            <p className="text-[10px] font-bold text-cs-muted uppercase tracking-tighter">Route Summary</p>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-cs-dark">{load.first_stop?.name}</span>
                                <span className="text-cs-muted">→</span>
                                <span className="text-sm font-bold text-cs-dark">{load.last_stop?.name}</span>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-cs-bg-alt">
                        <div>
                            <p className="text-[9px] text-cs-muted uppercase font-bold">Total Distance</p>
                            <p className="text-xs font-bold text-cs-dark">{load.practical_miles} miles</p>
                        </div>
                        <div className="text-right">
                            <p className="text-[9px] text-cs-muted uppercase font-bold">Estimated Arrival</p>
                            <p className="text-xs font-bold text-cs-dark">{load.delivery_date?.slice(11, 16)} hrs</p>
                        </div>
                    </div>
                </div>

                {/* ITINERARY */}
                <section>
                    <div className="flex items-center justify-between mb-3 ml-1">
                        <h3 className="text-[11px] font-bold text-cs-muted uppercase tracking-[0.15em]">Itinerary</h3>
                        <span className="text-[9px] text-cs-muted font-medium bg-cs-bg-alt px-2 py-0.5 rounded-full border border-cs-border/50">
                            Tap stop to action
                        </span>
                    </div>

                    <div className="space-y-3">
                        {load.stops?.map((stop, i) => {
                            const isNext = !isLoadCompleted && stop.id === nextStop?.id;
                            const stopStateMeta = getStopStateMeta(stop, isNext);
                            return (
                                <div
                                    key={stop.id}
                                    onClick={() => navigate(`/active-stop/${id}/${stop.id}`)}
                                    className={`relative flex items-center gap-4 p-4 rounded-3xl transition-all border ${isNext ? 'bg-white border-cs-primary shadow-md scale-[1.02] z-10' : 'bg-white/60 border-cs-border/40 opacity-80'
                                        } active:scale-[0.98]`}
                                >
                                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm ${isNext ? 'bg-cs-primary text-white shadow-lg shadow-cs-primary/30' : 'bg-cs-bg-alt text-cs-muted'
                                        }`}>
                                        {i + 1}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className={`text-sm font-bold ${isNext ? 'text-cs-dark' : 'text-cs-muted'}`}>{stop.name}</p>
                                            {isNext && <span className="text-[8px] bg-cs-primary text-white px-1.5 py-0.5 rounded font-black uppercase">Next</span>}
                                            <span className={`text-[8px] px-1.5 py-0.5 rounded font-black uppercase ${stopStateMeta.badgeClass}`}>
                                                {stopStateMeta.badge}
                                            </span>
                                        </div>
                                        <p className="text-[10px] font-bold text-cs-muted uppercase tracking-tight">{stop.statusOperator?.name || "Pending"}</p>
                                        <p className="text-[11px] text-cs-muted mt-1">{stopStateMeta.helper}</p>
                                    </div>
                                    <FaChevronRight size={12} className={isNext ? "text-cs-primary" : "text-cs-border"} />
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* EVIDENCES */}
                <section>
                    <h3 className="text-[11px] font-bold text-cs-muted mb-3 ml-1 uppercase tracking-[0.15em]">Evidences</h3>
                    <div className="bg-white rounded-3xl shadow-soft p-2.5 flex flex-col gap-2 border border-white">
                        {load.evidences?.length ? (
                            load.evidences.map((e, i) => (
                                <div key={i} className="flex items-center justify-between p-3 rounded-2xl bg-cs-bg-alt/40">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center text-cs-primary shadow-sm">
                                            <FaFileAlt size={14} />
                                        </div>
                                        <div className="max-w-[150px]">
                                            <p className="text-sm font-bold text-cs-dark truncate">{e.name}</p>
                                            <p className="text-[10px] text-cs-muted truncate tracking-tight">{e.description || 'No description'}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setModal({ show: true, type: 2, url: e.base64, title: e.name })}
                                        className="px-4 py-2 rounded-xl bg-cs-primary text-white text-[10px] font-bold active:scale-90 transition-transform shadow-sm"
                                    >
                                        VIEW
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="py-8 text-center"><p className="text-xs text-cs-muted font-medium italic">No files attached</p></div>
                        )}
                    </div>
                </section>

                {/* INCIDENTS / REPORTS */}
                <section className="pb-10">
                    <div className="flex items-center justify-between mb-3 ml-1">
                        <h3 className="text-[11px] font-bold text-cs-muted uppercase tracking-[0.15em]">Incidents</h3>
                        <button
                            onClick={newReport}
                            className="flex items-center gap-2 bg-cs-danger text-white px-3.5 py-1.5 rounded-full text-[10px] font-bold shadow-lg shadow-cs-danger/20 active:scale-95 transition-transform"
                        >
                            <FaExclamationTriangle size={10} /> LOG ISSUE
                        </button>
                    </div>
                    <div className="bg-white rounded-3xl shadow-soft p-2.5 flex flex-col gap-2 border border-white">
                        {reports.length > 0 ? reports.map((r) => (
                            <div
                                key={r.id}
                                onClick={() => navigate(`/detail-report/${r.id}`)}
                                className="flex items-center gap-4 p-3 rounded-2xl bg-cs-danger/[0.03] border border-cs-danger/10 active:scale-[0.99] transition-all"
                            >
                                <div className="w-1.5 h-8 rounded-full" style={{ background: r.status?.color }} />
                                <div className="flex-1">
                                    <p className="text-sm font-bold text-cs-dark tracking-tight">Report #{r.id}</p>
                                    <p className="text-[10px] font-extrabold text-cs-danger uppercase tracking-wider">{r.status?.name}</p>
                                </div>
                                <FaChevronRight size={10} className="text-cs-danger/30" />
                            </div>
                        )) : (
                            <div className="py-6 text-center"><p className="text-xs text-cs-muted italic font-medium">No incidents reported</p></div>
                        )}
                    </div>
                </section>
            </div>

            {/* MODALS */}
            {modal.show && <PreviewPDF modal={modal} setModal={setModal} />}
            {modalReport.show && (
                <ReportModal
                    modal={modalReport}
                    setModal={setModalReport}
                    filters={filters}
                    setFilters={setFilters}
                />
            )}
        </div>
    );
};

export default LoadDetail;